import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DSLinkButton } from '@stihl-design-system/components';

import { link } from '@phoenix/core/frontend/shared/util/navigation/link.utils';
import { BaseBanner } from '@phoenix/team-m/dashboard/frontend/shared/components/base-banner';
import { LinkOut } from '@phoenix/core/frontend/shared/ui/link-out/link-out';
import { GA } from '@phoenix/core/frontend/shared/feature/google-analytics/ga';
import { useImpressionEvent } from '@phoenix/core/frontend/shared/util/hooks/use-impression-event';
import { WarrantyExtensionEntry } from '@phoenix/core/frontend/shared/domain/static-content/static-content-api.model';
import { WarrantyExtensionConditionsDialog } from './warranty-extension-conditions-dialog';

interface WarrantyExtensionBannerProps {
  hasLinkButton?: boolean;
  legalConditions?: WarrantyExtensionEntry;
  preffix?: 'addTool' | 'dashboard';
  trackEvents?: boolean;
}

export const WarrantyExtensionBanner: React.FC<WarrantyExtensionBannerProps> = ({
  hasLinkButton,
  legalConditions,
  preffix = 'addTool',
  trackEvents,
}) => {
  const { t } = useTranslation();
  const [showConditionsDialog, setShowConditionsDialog] = useState(false);
  const BASE_EVENT_NAME = `mystihl_banner_${preffix}_warranty_extension`;
  const { generalWarrantyConditionsUrl, additionalInformation } = legalConditions || {};

  const headline = t(`${preffix}_bannerExtendWarranty_header`);

  const onAddToolClick = () => {
    trackEvents &&
      GA.sendEvent({
        event_name: `${BASE_EVENT_NAME}_link_click`,
        ...trackingData,
      });
  };

  const linkButton = hasLinkButton
    ? {
        url: link('tool-shed/add-tool'),
        label: t(`${preffix}_bannerExtendWarranty_buttonLabel`),
      }
    : undefined;

  const trackingData = {
    id: `${preffix}_warranty_extension_banner`,
    headline,
    description: t(`${preffix}_bannerExtendWarranty_description`),
    button_text: linkButton?.label,
    link_url: linkButton?.url,
  };

  const ref = useImpressionEvent(() => trackEvents && GA.sendEvent({ event_name: `${BASE_EVENT_NAME}_impression`, ...trackingData }));

  const getDescription = () => {
    const hasAdditionalInformation = !!additionalInformation;
    const i18nKey = `${preffix}_bannerExtendWarranty_description${hasAdditionalInformation ? '_twoLinks' : ''}`;

    const transComponents: Record<string, React.ReactElement> = {
      a: <LinkOut href={generalWarrantyConditionsUrl || '#'} allowReferrer={true} />,
      ...(hasAdditionalInformation && {
        button: <button type="button" className="inline-link" onClick={() => setShowConditionsDialog(true)} />,
      }),
    };

    return <Trans i18nKey={i18nKey} components={transComponents} />;
  };

  return (
    <>
      <BaseBanner
        ref={ref}
        id="warranty-extension-banner"
        backgroundImg="warranty-extension.webp"
        imageTags={['imagesToolShed', 'imagesDashboard']}
        headline={headline}
        description={getDescription()}
      >
        {linkButton && (
          <DSLinkButton href={linkButton.url} onClick={onAddToolClick} iconName="plus" className="mt-5 w-full tablet:w-fit">
            {linkButton.label}
          </DSLinkButton>
        )}
      </BaseBanner>
      <WarrantyExtensionConditionsDialog
        preffix={preffix}
        additionalInformation={additionalInformation}
        isOpen={showConditionsDialog}
        onClose={() => setShowConditionsDialog(false)}
      />
    </>
  );
};

export default WarrantyExtensionBanner;
